/**
 * FieldForms -- Field-optimized ICS form list with offline support.
 *
 * Shows forms for the active incident, with offline drafting capability.
 * Uses the useOfflineForms hook for automatic online/offline switching.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIncidentStore } from '@/store/incident.store';
import { useOfflineForms } from '@/hooks/useOfflineForms';
import { FORM_STATUS_LABELS, FORM_STATUS_COLORS } from '@/types/form';
import type { FormStatus } from '@/types/form';
import type { OfflineForm, CachedFormTemplate } from '@/offline/db';
import { toast } from '@/utils/toast';
import FaIcon from '@/components/common/FaIcon';
import EmptyState from '@/components/common/EmptyState';
import LoadingState from '@/components/common/LoadingState';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import FormField from '@/components/common/FormField';
import Badge from '@/components/common/Badge';
import TabButton from '@/components/common/TabButton';
import OfflineSyncIndicator from '@/components/common/OfflineSyncIndicator';
import TimeAgo from '@/components/common/TimeAgo';

type StatusFilter = 'all' | 'draft' | 'pending_signature' | 'approved';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'pending_signature', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
];

export default function FieldForms() {
  const navigate = useNavigate();
  const { activeIncident } = useIncidentStore();
  const {
    forms,
    templates,
    isLoading,
    isOffline,
    isSyncing,
    unsyncedCount,
    syncNow,
    createForm,
  } = useOfflineForms();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Filter forms by status
  const filteredForms = useMemo(() => {
    if (statusFilter === 'all') return forms;
    return forms.filter((f) => f.status === statusFilter);
  }, [forms, statusFilter]);

  // Group templates by category for the create form
  const templatesByCategory = useMemo(() => {
    const groups: Record<string, CachedFormTemplate[]> = {};
    for (const t of templates) {
      const cat = t.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(t);
    }
    return groups;
  }, [templates]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const tpl = templates.find((t) => t.id === templateId);
    if (tpl && activeIncident) {
      setFormTitle(`${tpl.code} - ${activeIncident.name}`);
    } else if (tpl) {
      setFormTitle(`${tpl.code} - ${tpl.name}`);
    }
  };

  const handleCreate = async () => {
    if (!selectedTemplate || !formTitle.trim()) {
      toast.error('Please select a template and enter a title');
      return;
    }

    setIsCreating(true);
    try {
      await createForm({
        template_id: selectedTemplate.id,
        template_name: selectedTemplate.name,
        incident_id: activeIncident?.id || null,
        title: formTitle.trim(),
      });

      toast.success(
        isOffline
          ? 'Form draft saved offline -- will sync when back online'
          : 'Form created'
      );
      setShowCreate(false);
      setSelectedTemplateId('');
      setFormTitle('');
    } catch {
      toast.error('Failed to create form');
    } finally {
      setIsCreating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const label =
      FORM_STATUS_LABELS[status as FormStatus] || status;
    const colorClass =
      FORM_STATUS_COLORS[status as FormStatus] ||
      'bg-gray-100 text-gray-800';
    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}
      >
        {label}
      </span>
    );
  };

  const getSyncBadge = (form: OfflineForm) => {
    if (form.is_synced === 0) {
      return (
        <Badge variant="warning">
          <FaIcon icon="clock" className="mr-1 h-3 w-3" />
          Unsynced
        </Badge>
      );
    }
    return null;
  };

  if (isLoading) {
    return <LoadingState message="Loading forms..." />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-theme-primary">
            ICS Forms
          </h2>
          <OfflineSyncIndicator
            isOffline={isOffline}
            unsyncedCount={unsyncedCount}
            isSyncing={isSyncing}
            onSync={syncNow}
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowCreate(true)}
          disabled={templates.length === 0}
        >
          <FaIcon icon="plus" className="mr-1.5 h-3.5 w-3.5" />
          New Form
        </Button>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1 border-b border-theme-primary">
        {STATUS_FILTERS.map((filter) => (
          <TabButton
            key={filter.value}
            active={statusFilter === filter.value}
            onClick={() => setStatusFilter(filter.value)}
          >
            {filter.label}
          </TabButton>
        ))}
      </div>

      {/* Create form panel */}
      {showCreate && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-theme-primary">
              Create New Form
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowCreate(false);
                setSelectedTemplateId('');
                setFormTitle('');
              }}
            >
              <FaIcon icon="xmark" className="h-4 w-4" />
            </Button>
          </div>

          <FormField label="Template" htmlFor="template-select" required>
            <Input
              as="select"
              id="template-select"
              value={selectedTemplateId}
              onChange={(e) =>
                handleSelectTemplate(
                  (e.target as HTMLSelectElement).value
                )
              }
            >
              <option value="">Select a template...</option>
              {Object.entries(templatesByCategory).map(
                ([category, categoryTemplates]) => (
                  <optgroup key={category} label={category}>
                    {categoryTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.code} - {t.name}
                      </option>
                    ))}
                  </optgroup>
                )
              )}
            </Input>
          </FormField>

          <FormField label="Title" htmlFor="form-title" required>
            <Input
              id="form-title"
              value={formTitle}
              onChange={(e) =>
                setFormTitle((e.target as HTMLInputElement).value)
              }
              placeholder="Enter form title"
            />
          </FormField>

          <div className="mt-3 flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowCreate(false);
                setSelectedTemplateId('');
                setFormTitle('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreate}
              disabled={isCreating || !selectedTemplateId || !formTitle.trim()}
            >
              {isCreating ? (
                <>
                  <FaIcon
                    icon="spinner"
                    className="mr-1.5 h-3.5 w-3.5 animate-spin"
                  />
                  Creating...
                </>
              ) : (
                <>
                  <FaIcon icon="plus" className="mr-1.5 h-3.5 w-3.5" />
                  Create Draft
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Forms list */}
      {filteredForms.length === 0 ? (
        <EmptyState
          variant="operational"
          icon="file-lines"
          title={
            statusFilter === 'all'
              ? 'No forms yet'
              : `No ${STATUS_FILTERS.find((f) => f.value === statusFilter)?.label?.toLowerCase()} forms`
          }
          description={
            isOffline
              ? 'Forms will appear here once cached. Go online to fetch forms.'
              : 'Create a new form to get started.'
          }
          actionLabel={!isOffline ? 'New Form' : undefined}
          onAction={!isOffline ? () => setShowCreate(true) : undefined}
        />
      ) : (
        <div className="space-y-2">
          {filteredForms.map((form) => (
            <Button
              key={form.localId ?? form.offline_id}
              onClick={() => {
                // Navigate to the form detail if we have a server ID
                if (form.id && form.id !== form.offline_id) {
                  navigate(`/forms/${form.id}`);
                } else {
                  toast.info(
                    'This form is only available offline. It will be accessible after syncing.'
                  );
                }
              }}
              variant="ghost"
              fullWidth
              className="!justify-start !text-left rounded-lg border border-theme-primary bg-surface-primary p-3 min-h-[44px] hover:bg-surface-secondary active:bg-surface-secondary h-auto"
            >
              <div className="w-full">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-theme-primary">
                      {form.title}
                    </p>
                    <p className="mt-0.5 text-xs text-theme-tertiary">
                      {form.template_name}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {getStatusBadge(form.status)}
                    {getSyncBadge(form)}
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-3 text-xs text-theme-tertiary">
                  <span className="inline-flex items-center">
                    <FaIcon icon="clock" className="mr-1 h-3 w-3" />
                    <TimeAgo timestamp={form.updated_at} />
                  </span>
                  {form.last_error && (
                    <span className="text-red-500">
                      <FaIcon
                        icon="triangle-exclamation"
                        className="mr-1 h-3 w-3"
                      />
                      Sync error
                    </span>
                  )}
                </div>
              </div>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
